import { createSelector } from "reselect";
import AppState from "../AppState";
import {
  PLATFORM_DEFAULTS,
  PLATFORM_INDEPENDENT_DEFAULTS,
} from "../../utils/DefaultConfigs";
import { getConfigValue } from "../../utils/AppConfigUtils";
import { AppConfigDetails } from "./AppConfigTypes";
import { selectCurrentStoreId } from "../store-list/StoreListSelectors";
export const selectAppConfigurations = (state: AppState): AppConfigDetails => {
  return state.appConfig.data;
};

export const selectAppLogo = createSelector(
  [selectAppConfigurations],
  (appConfigDetails) => {
    const storeWithLogo = appConfigDetails.stores.find((s) => s.logo);
    if (storeWithLogo) {
      return storeWithLogo.logo;
    } else {
      return appConfigDetails.account?.logo || "";
    }
  },
);

export const selectConfigValue = (state: AppState, configKey: string) => {
  const platformType =
    state.adapterConfig?.AdapterInfo?.adapterId.toUpperCase() ?? "";
  const platformDefaults = PLATFORM_DEFAULTS[platformType];
  const currentStoreId = selectCurrentStoreId(state);
  const appConfigs = state.appConfig?.storeAndAccountConfigDetails;
  const platformDefaultConfigValue = platformDefaults?.[configKey] ?? "";
  const platformIndependentConfigValue =
    PLATFORM_INDEPENDENT_DEFAULTS?.[configKey] ?? "";

  return getConfigValue(
    appConfigs,
    currentStoreId,
    configKey,
    platformDefaultConfigValue,
    platformIndependentConfigValue,
  );
};

export const selectCallerAccountId = (state: AppState) => {
  return state.appConfig?.accountId;
};

export const selectCallerAccounts = (state: AppState) => {
  return state.appConfig?.callerAccounts;
};

export const selectIsCallerAccountsInvoked = (state: AppState) => {
  return state.appConfig?.isCallerAccountsInvoked;
};

export const selectIsFetchingCallerAccountsFailed = (state: AppState) => {
  return state.appConfig?.isFetchingCallerAccountsFailed;
};

export const selectProductImagePrefix = (
  state: AppState,
  currentStoreId: string | null,
) => {
  const { appConfig } = state;
  if (!appConfig || !appConfig.data) {
    return "";
  }
  const { stores, account } = appConfig.data;
  // Find the store product image prefix
  const storeProductImagePrefix = stores?.find(
    (store) => store.storeId === currentStoreId && store.productImagePrefix,
  )?.productImagePrefix;
  // Find the account product image prefix
  const accountProductImagePrefix = account?.productImagePrefix;
  return storeProductImagePrefix ?? accountProductImagePrefix ?? "";
};

export const selectAccountFeatures = (state: AppState, accountId: string) => {
  return (
    state.appConfig?.callerAccounts.accounts.find(
      (account) => account.accountId === accountId,
    )?.features ?? {}
  );
};

export const selectCurrentAccountFeatures = (state: AppState) => {
  const accountId = selectCallerAccountId(state);
  return selectAccountFeatures(state, accountId);
};

export const selectIsAccountFeatureEnabled = (feature: string) =>
  createSelector(
    [selectCurrentAccountFeatures],
    (features) => features?.[feature] ?? false,
  );

export const selectIsLinkedCategoryFeatureEnabled = (state: AppState) => {
  const accountId = selectCallerAccountId(state);
  const features = selectAccountFeatures(state, accountId);
  return features?.["linked-category"] ?? false;
};

export const selectIsLinkCategoryToolFeatureEnabled = (state: AppState) => {
  const accountId = selectCallerAccountId(state);
  const features = selectAccountFeatures(state, accountId);
  return features?.["link-category-tool"] ?? false;
};

export const selectIsShowImgMgmtFeatureEnabled = (state: AppState) => {
  const accountId = selectCallerAccountId(state);
  const features = selectAccountFeatures(state, accountId);
  return features?.["show-image-mgmt"] ?? false;
};

export const selectIsProductReindexFeatureEnabled = (state: AppState) => {
  const accountId = selectCallerAccountId(state);
  const features = selectAccountFeatures(state, accountId);
  return features?.["product-reindex"] ?? false;
};

export const selectIsKPIExecutiveSummaryFeatureEnabled = (state: AppState) => {
  const accountId = selectCallerAccountId(state);
  const features = selectAccountFeatures(state, accountId);
  return features?.["kpi-executive-summary"] ?? false;
};

export const selectAccountEntitlements = (
  state: AppState,
  accountId: string,
) => {
  const account = state.appConfig?.callerAccounts.accounts.find(
    (acc) => acc.accountId === accountId,
  );
  return account?.entitlements ?? [];
};

export const selectCurrentAccountEntitlements = (state: AppState) => {
  const accountId = selectCallerAccountId(state);
  return selectAccountEntitlements(state, accountId);
};

export const selectHasEntitlement =
  (entitlement: string) => (state: AppState) => {
    const entitlements = selectCurrentAccountEntitlements(state);
    return entitlements.includes(entitlement);
  };

export const selectHasAllEntitlements =
  (requiredEntitlements: string[]) => (state: AppState) => {
    const entitlements = selectCurrentAccountEntitlements(state);
    return requiredEntitlements.every((required) =>
      entitlements.includes(required),
    );
  };

  export const selectIsKPIChatFeatureEnabled = (state: AppState) => {
    const accountId = selectCallerAccountId(state);
    const features = selectAccountFeatures(state, accountId);
    return features?.["kpi-chat"] ?? false;
  };

  export const selectIsPagePositionFeatureEnabled = (state: AppState) => {
    const accountId = selectCallerAccountId(state);
    const features = selectAccountFeatures(state, accountId);
    return features?.["page-position"] ?? false;
  };
